import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import { CardNB, ButtonNB, IconNB } from '../../../components/ui-nb';
import EmployeeInviteModal from '../../../components/EmployeeInviteModal';
import type { Tables } from '@tellowup/db';
import { error as logError } from '../../../lib/logger';

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'pending';
  code?: string;
  expires_at?: string;
}

// Narrow types for specific Supabase query result shapes
type ProfileSummary = Pick<Tables<'profiles'>, 'id' | 'full_name' | 'email' | 'role'>;
type InvitationSummary = Pick<Tables<'invitation'>, 'id' | 'email' | 'role' | 'code' | 'expires_at'>;

export default function InviteUser() {
  const { lang } = useParams();
  const locale = lang || 'en';
  const organizationId = useAuthStore(s => s.user?.organizationId);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [qrInvite, setQrInvite] = useState<UserRow | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) return;
    async function fetchData() {
      try {
        setFetchError(null);
        const [profilesRes, invitesRes] = await Promise.all([
          supabase
            .from('profiles')
            .select('id, full_name, email, role')
            .eq('organization_id', organizationId),
          supabase
            .from('invitation')
            .select('*')
            .eq('organization_id', organizationId)
            .is('branch_id', null)
            .is('accepted_at', null),
        ]);

        if (profilesRes.error) {
          logError('Profiles fetch error', { error: profilesRes.error.message });
          setFetchError('Failed to load members: ' + profilesRes.error.message);
        }
        if (invitesRes.error) {
          logError('Invitations fetch error', { error: invitesRes.error.message });
          setFetchError((prev) => prev ? prev : 'Failed to load invitations: ' + invitesRes.error.message);
        }

        const active: UserRow[] = (profilesRes.data ?? []).map((p: ProfileSummary) => ({
          id: p.id,
          name: p.full_name || '—',
          email: p.email || '',
          role: p.role || 'technician',
          status: 'active' as const,
        }));

        const pending: UserRow[] = (invitesRes.data ?? []).map((inv: InvitationSummary) => ({
          id: inv.id,
          name: inv.email,
          email: inv.email || '',
          role: inv.role || 'technician',
          status: 'pending' as const,
          code: inv.code,
          expires_at: inv.expires_at,
        }));

        setUsers([...active, ...pending]);
      } catch (err) {
        logError('Error fetching data', { error: err instanceof Error ? err.message : String(err) });
        setFetchError('Unexpected error loading data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [organizationId]);

  const handleRevokeInvite = async (id: string) => {
    if (!window.confirm('Revoke this invitation?')) return;
    setRevokingId(id);
    try {
      const { error } = await supabase.from('invitation').delete().eq('id', id);
      if (error) throw error;
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      logError('Error revoking invitation', { error: err instanceof Error ? err.message : String(err) });
      alert('Failed to revoke invitation');
    } finally {
      setRevokingId(null);
    }
  };

  const handleResendInvite = async (invite: UserRow) => {
    if (!invite.id) return;
    setResendingId(invite.id);
    try {
      // Generate new code and extend expiry
      const newCode = Array.from({ length: 8 }, () =>
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)]
      ).join('');

      const newExpiresAt = new Date();
      newExpiresAt.setDate(newExpiresAt.getDate() + 7);

      const { error } = await supabase
        .from('invitation')
        .update({
          code: newCode,
          expires_at: newExpiresAt.toISOString(),
        })
        .eq('id', invite.id);

      if (error) throw error;

      setUsers((prev) =>
        prev.map((u) =>
          u.id === invite.id ? { ...u, code: newCode, expires_at: newExpiresAt.toISOString() } : u
        )
      );

      // Refresh QR if this invite is showing
      if (qrInvite?.id === invite.id) {
        const { generateInviteQR } = await import('../../../lib/qr-code');
        const qrUrl = await generateInviteQR(newCode);
        setQrDataUrl(qrUrl);
      }
    } catch (err) {
      logError('Error resending invitation', { error: err instanceof Error ? err.message : String(err) });
      alert('Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const handleInviteSuccess = () => {
    setShowInviteModal(false);
    if (!organizationId) return;
    async function refresh() {
      const { data: invitesRes } = await supabase
        .from('invitation')
        .select('*')
        .eq('organization_id', organizationId)
        .is('branch_id', null)
        .is('accepted_at', null);

      const pending: UserRow[] = (invitesRes ?? []).map((inv: Tables<'invitation'>) => ({
        id: inv.id,
        name: inv.email,
        email: inv.email || '',
        role: inv.role || 'technician',
        status: 'pending' as const,
        code: inv.code,
      }));

      setUsers((prev) => {
        const active = prev.filter((u) => u.status === 'active');
        return [...active, ...pending];
      });
    }
    refresh();
  };

  const handleShowQR = async (invite: UserRow) => {
    if (!invite.code) return;
    setQrInvite(invite);
    const { generateInviteQR } = await import('../../../lib/qr-code');
    const qrUrl = await generateInviteQR(invite.code);
    setQrDataUrl(qrUrl);
  };

  const handleCloseQR = () => {
    setQrInvite(null);
    setQrDataUrl(null);
  };

  const handleCopyQRPath = () => {
    if (!qrInvite?.code) return;
    const url = `${window.location.origin}/${locale}/join/${qrInvite.code}`;
    navigator.clipboard.writeText(url);
  };

  const activeUsers = users.filter((u) => u.status === 'active');
  const pendingUsers = users.filter((u) => u.status === 'pending');

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-nb-black">
            Users
          </h1>
          <p className="text-nb-text-muted text-sm mt-1">
            Manage organization members and pending invitations
          </p>
        </div>
        <ButtonNB variant="primary" onClick={() => setShowInviteModal(true)}>
          <IconNB name="plus" size={16} />
          Invite User
        </ButtonNB>
      </div>

      {fetchError && (
        <div className="bg-nb-red/10 text-nb-red p-3 rounded mb-4 text-sm">
          {fetchError}
        </div>
      )}

      {loading ? (
        <p className="text-nb-text-muted">Loading...</p>
      ) : users.length === 0 && !fetchError ? (
        <CardNB className="p-8 text-center">
          <p className="text-nb-text-muted">No users yet. Click "Invite User" to get started.</p>
        </CardNB>
      ) : (
        <CardNB className="p-6">
          <div className="flex flex-col gap-1">
            {activeUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between py-2.5 border-b border-nb-black/10"
              >
                <div className="flex flex-col">
                  <span className="font-nb font-medium text-nb-black">{user.name}</span>
                  <span className="text-xs text-nb-text-muted">{user.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-green/10 text-green px-2 py-0.5 rounded-sm capitalize">
                    Active
                  </span>
                  <span className="text-xs text-nb-text-muted capitalize">
                    {user.role}
                  </span>
                </div>
              </div>
            ))}

            {pendingUsers.map((user) => (
              <div
                key={user.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2.5 border-b border-nb-black/10 gap-2"
              >
                <div className="flex flex-col min-w-0">
                  <span className="font-nb font-medium text-nb-black truncate">{user.name}</span>
                  <span className="text-xs text-nb-text-muted">Invitation sent</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  <span className="text-xs bg-yellow/10 text-yellow-700 px-2 py-0.5 rounded-sm capitalize">
                    Pending
                  </span>
                  <span className="text-xs text-nb-text-muted capitalize">
                    {user.role}
                  </span>
                  <button
                    onClick={() => handleShowQR(user)}
                    disabled={!user.code}
                    title="Show QR code"
                    className="min-w-[32px] min-h-[32px] flex items-center justify-center disabled:opacity-50"
                  >
                    <IconNB name="globe" size={16} />
                  </button>
                  <button
                    onClick={() => handleResendInvite(user)}
                    disabled={resendingId === user.id}
                    title="Resend invitation"
                    className="min-w-[32px] min-h-[32px] flex items-center justify-center text-nb-black opacity-75 disabled:opacity-50"
                  >
                    <IconNB name="refresh" size={16} />
                  </button>
                  <button
                    onClick={() => handleRevokeInvite(user.id)}
                    disabled={revokingId === user.id}
                    title="Revoke invitation"
                    className="min-w-[32px] min-h-[32px] flex items-center justify-center text-nb-red opacity-75 disabled:opacity-50"
                  >
                    <IconNB name="close" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardNB>
      )}

      {showInviteModal && organizationId && (
        <EmployeeInviteModal
          organizationId={organizationId}
          locale={locale}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}

      {qrInvite && qrDataUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && handleCloseQR()}
        >
          <CardNB className="max-w-80 w-full p-6 text-center">
            <h3 className="text-lg font-bold text-nb-black mb-1">
              QR Code for {qrInvite.email}
            </h3>
            <p className="text-nb-text-muted text-xs mb-4">
              {qrInvite.role} · Pending
            </p>
            <img
              src={qrDataUrl}
              alt="QR Code"
              className="w-44 h-44 mx-auto border-3 border-nb-black rounded-[2px] mb-4"
            />
            <p className="text-nb-text-muted text-xs mb-4 break-all">
              {window.location.origin}/{locale}/join/{qrInvite.code}
            </p>
            <div className="flex gap-2">
              <ButtonNB variant="secondary" onClick={handleCopyQRPath} className="flex-1">
                Copy link
              </ButtonNB>
              <ButtonNB variant="secondary" onClick={handleCloseQR} className="flex-1">
                Close
              </ButtonNB>
            </div>
          </CardNB>
        </div>
      )}
    </>
  );
}